import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Hidden,
} from '@material-ui/core';
import { makeStyles, withStyles } from '@material-ui/core/styles';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import ListAltIcon from '@material-ui/icons/ListAlt';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import CategoryIcon from '@material-ui/icons/Category';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import ErrorIcon from '@material-ui/icons/Error';
import Layout from '../components/Layout/Layout';
import apiService from '../services/api';

const useStyles = makeStyles((theme) => ({
  statCard: {
    height: '100%',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
    },
  },
  statCardContent: {
    padding: theme.spacing(2),
    '&:last-child': {
      paddingBottom: theme.spacing(2),
    },
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2, 3),
    },
  },
  statIcon: {
    fontSize: 32,
    opacity: 0.8,
    [theme.breakpoints.up('sm')]: {
      fontSize: 48,
    },
  },
  statValue: {
    fontSize: 24,
    fontWeight: 700,
    [theme.breakpoints.up('sm')]: {
      fontSize: 32,
    },
  },
  statLabel: {
    fontSize: 12,
    color: '#666',
    fontWeight: 500,
    [theme.breakpoints.up('sm')]: {
      fontSize: 14,
    },
  },
  ordersCard: {
    background: 'linear-gradient(135deg, #1976d2 0%, #1565c0 100%)',
    color: '#fff',
  },
  activeOrdersCard: {
    background: 'linear-gradient(135deg, #2e7d32 0%, #1b5e20 100%)',
    color: '#fff',
  },
  alertsCard: {
    background: 'linear-gradient(135deg, #f47d06 0%, #c45f00 100%)',
    color: '#fff',
  },
  catalogsCard: {
    background: 'linear-gradient(135deg, #7b1fa2 0%, #4a148c 100%)',
    color: '#fff',
  },
  sectionTitle: {
    marginBottom: theme.spacing(2),
    fontWeight: 600,
    fontSize: '1rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1.25rem',
    },
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    overflowX: 'auto',
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  statusActive: {
    backgroundColor: '#e8f5e9',
    color: '#2e7d32',
  },
  statusClosed: {
    backgroundColor: '#e3f2fd',
    color: '#1976d2',
  },
  statusFailed: {
    backgroundColor: '#ffebee',
    color: '#c62828',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
  },
  tokenStatusContainer: {
    marginTop: theme.spacing(2),
    [theme.breakpoints.up('sm')]: {
      marginTop: theme.spacing(3),
    },
  },
  tokenCard: {
    padding: theme.spacing(1.5),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(2),
    },
  },
  tokenName: {
    fontSize: '0.875rem',
    [theme.breakpoints.up('sm')]: {
      fontSize: '1rem',
    },
  },
  // Mobile card view for orders
  mobileOrderCard: {
    marginBottom: theme.spacing(1.5),
    padding: theme.spacing(2),
  },
  mobileOrderRow: {
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: theme.spacing(0.5),
    '&:last-child': {
      marginBottom: 0,
    },
  },
  mobileOrderLabel: {
    color: '#666',
    fontSize: '0.75rem',
    fontWeight: 500,
  },
  mobileOrderValue: {
    fontSize: '0.875rem',
    fontWeight: 500,
    textAlign: 'right',
  },
  mobileOrderHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    borderBottom: '1px solid #eee',
  },
}));

const StyledTableCell = withStyles(() => ({
  head: {
    backgroundColor: '#f5f5f5',
    fontWeight: 600,
    whiteSpace: 'nowrap',
  },
  body: {
    fontSize: '0.875rem',
  },
}))(TableCell);

function Dashboard() {
  const classes = useStyles();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    activeOrders: 0,
    totalAlerts: 0,
    totalCatalogs: 0,
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [dpUserIds, setDpUserIds] = useState([]);
  const [tokenStatuses, setTokenStatuses] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      // Use V2 APIs for role-based filtering (Admin sees all, Users see own)
      const [orders, activeOrders, alerts, catalogs, dpIds] = await Promise.all([
        apiService.getOrdersV2(),
        apiService.getActiveOrdersV2(),
        apiService.getAlerts(),
        apiService.getCatalogs(),
        apiService.getDpUserIds(),
      ]);

      setStats({
        totalOrders: orders.length,
        activeOrders: activeOrders.length,
        totalAlerts: alerts.length,
        totalCatalogs: catalogs.length,
      });

      setRecentOrders(orders.slice(0, 10));
      setDpUserIds(dpIds);

      // Fetch token statuses for all users
      const statuses = {};
      for (const dpId of dpIds) {
        try {
          const status = await apiService.getTokenStatus(dpId);
          statuses[dpId] = status.token_status;
        } catch (e) {
          statuses[dpId] = 'NOT_ACTIVE';
        }
      }
      setTokenStatuses(statuses);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const getStatusChip = (status) => {
    switch (status) {
      case 'ACTIVE':
        return <Chip size="small" label="Active" className={classes.statusActive} />;
      case 'CLOSED':
        return <Chip size="small" label="Closed" className={classes.statusClosed} />;
      case 'FAILED':
        return <Chip size="small" label="Failed" className={classes.statusFailed} />;
      default:
        return <Chip size="small" label={status || 'N/A'} />;
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Mobile order card component
  const MobileOrderCard = ({ order }) => (
    <Card className={classes.mobileOrderCard}>
      <Box className={classes.mobileOrderHeader}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {order.script_name}
        </Typography>
        {getStatusChip(order.status)}
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Provider</Typography>
        <Typography className={classes.mobileOrderValue}>{order.app_provider}</Typography>
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>User</Typography>
        <Typography className={classes.mobileOrderValue}>{order.user_dp_id}</Typography>
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Catalog</Typography>
        <Typography className={classes.mobileOrderValue}>{order.catalog_name}</Typography>
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Side</Typography>
        <Chip
          size="small"
          label={order.transaction_side}
          style={{
            backgroundColor: order.transaction_side === 'LONG' ? '#e8f5e9' : '#ffebee',
            color: order.transaction_side === 'LONG' ? '#2e7d32' : '#c62828',
            height: 20,
            fontSize: '0.75rem',
          }}
        />
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Entry / Exit</Typography>
        <Typography className={classes.mobileOrderValue}>
          {order.entry_price || '-'} / {order.exit_price || '-'}
        </Typography>
      </Box>
      <Box className={classes.mobileOrderRow}>
        <Typography className={classes.mobileOrderLabel}>Time</Typography>
        <Typography className={classes.mobileOrderValue}>{formatDate(order.created_at)}</Typography>
      </Box>
    </Card>
  );

  if (loading) {
    return (
      <Layout title="Dashboard" onRefresh={fetchData}>
        <Box className={classes.loadingContainer}>
          <CircularProgress />
        </Box>
      </Layout>
    );
  }

  return (
    <Layout title="Dashboard" onRefresh={fetchData}>
      {/* Stats Cards */}
      <Grid container spacing={2}>
        <Grid item xs={6} sm={6} md={3}>
          <Card className={`${classes.statCard} ${classes.ordersCard}`}>
            <CardContent className={classes.statCardContent}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography className={classes.statValue}>{stats.totalOrders}</Typography>
                  <Typography className={classes.statLabel} style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Total Orders
                  </Typography>
                </Box>
                <ListAltIcon className={classes.statIcon} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card className={`${classes.statCard} ${classes.activeOrdersCard}`}>
            <CardContent className={classes.statCardContent}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography className={classes.statValue}>{stats.activeOrders}</Typography>
                  <Typography className={classes.statLabel} style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Active Orders
                  </Typography>
                </Box>
                <TrendingUpIcon className={classes.statIcon} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card className={`${classes.statCard} ${classes.alertsCard}`}>
            <CardContent className={classes.statCardContent}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography className={classes.statValue}>{stats.totalAlerts}</Typography>
                  <Typography className={classes.statLabel} style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Total Alerts
                  </Typography>
                </Box>
                <NotificationsActiveIcon className={classes.statIcon} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={6} sm={6} md={3}>
          <Card className={`${classes.statCard} ${classes.catalogsCard}`}>
            <CardContent className={classes.statCardContent}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography className={classes.statValue}>{stats.totalCatalogs}</Typography>
                  <Typography className={classes.statLabel} style={{ color: 'rgba(255,255,255,0.8)' }}>
                    Catalogs
                  </Typography>
                </Box>
                <CategoryIcon className={classes.statIcon} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Token Status */}
      <Box className={classes.tokenStatusContainer}>
        <Typography variant="h6" className={classes.sectionTitle}>
          Broker Token Status
        </Typography>
        <Grid container spacing={1}>
          {dpUserIds.map((dpId) => (
            <Grid item xs={6} sm={6} md={4} key={dpId}>
              <Card className={classes.tokenCard}>
                <Box display="flex" alignItems="center">
                  {tokenStatuses[dpId] === 'ACTIVE' ? (
                    <CheckCircleIcon style={{ color: '#2e7d32', marginRight: 6, fontSize: 20 }} />
                  ) : (
                    <ErrorIcon style={{ color: '#c62828', marginRight: 6, fontSize: 20 }} />
                  )}
                  <Typography className={classes.tokenName}>{dpId}</Typography>
                </Box>
                <Hidden xsDown>
                  <Chip
                    size="small"
                    label={tokenStatuses[dpId] || 'Unknown'}
                    style={{
                      backgroundColor: tokenStatuses[dpId] === 'ACTIVE' ? '#e8f5e9' : '#ffebee',
                      color: tokenStatuses[dpId] === 'ACTIVE' ? '#2e7d32' : '#c62828',
                      height: 24,
                      fontSize: '0.75rem',
                    }}
                  />
                </Hidden>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Recent Orders - Mobile Cards */}
      <Hidden smUp>
        <Box className={classes.tableContainer}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Recent Orders
          </Typography>
          {recentOrders.map((order, index) => (
            <MobileOrderCard key={order.id || index} order={order} />
          ))}
        </Box>
      </Hidden>

      {/* Recent Orders Table - Desktop */}
      <Hidden xsDown>
        <Box className={classes.tableContainer}>
          <Typography variant="h6" className={classes.sectionTitle}>
            Recent Orders
          </Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <StyledTableCell>Provider</StyledTableCell>
                  <StyledTableCell>User</StyledTableCell>
                  <StyledTableCell>Catalog</StyledTableCell>
                  <StyledTableCell>Symbol</StyledTableCell>
                  <StyledTableCell>Side</StyledTableCell>
                  <StyledTableCell>Entry</StyledTableCell>
                  <StyledTableCell>Exit</StyledTableCell>
                  <StyledTableCell>Time</StyledTableCell>
                  <StyledTableCell>Status</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentOrders.map((order, index) => (
                  <TableRow key={order.id || index} hover>
                    <TableCell>{order.app_provider}</TableCell>
                    <TableCell>{order.user_dp_id}</TableCell>
                    <TableCell>{order.catalog_name}</TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{order.script_name}</Typography>
                        <Typography variant="caption" color="textSecondary">
                          {order.instrument_type}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={order.transaction_side}
                        style={{
                          backgroundColor: order.transaction_side === 'LONG' ? '#e8f5e9' : '#ffebee',
                          color: order.transaction_side === 'LONG' ? '#2e7d32' : '#c62828',
                        }}
                      />
                    </TableCell>
                    <TableCell>{order.entry_price || '-'}</TableCell>
                    <TableCell>{order.exit_price || '-'}</TableCell>
                    <TableCell>{formatDate(order.created_at)}</TableCell>
                    <TableCell>{getStatusChip(order.status)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Hidden>
    </Layout>
  );
}

export default Dashboard;
